import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

type ApiClientOptions = {
    baseURL?: string;
    useWindowInstance?: boolean;
};

const apiInstances = new Map<string, AxiosInstance>();

const resolveBaseURL = (baseURL?: string): string => {
    if (baseURL && baseURL.trim().length > 0) {
        return baseURL;
    }

    return import.meta.env.VITE_API_URL || 'https://awbbackend.bliitz.in';
};

const attachAuthToken = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    if (typeof window !== 'undefined') {
        const token = window.localStorage.getItem('accessToken');
        if (token) {
            config.headers = config.headers ?? {};
            config.headers.Authorization = `Bearer ${token}`;
        }
    }

    return config;
};

export const getApiClient = (options: ApiClientOptions = {}): AxiosInstance => {
    if (options.useWindowInstance && typeof window !== 'undefined') {
        const windowInstance = (window as typeof window & { axiosInstance?: AxiosInstance }).axiosInstance;
        if (windowInstance) {
            return windowInstance;
        }
    }

    const baseURL = resolveBaseURL(options.baseURL);
    const existingInstance = apiInstances.get(baseURL);
    if (existingInstance) {
        return existingInstance;
    }

    const instance = axios.create({ baseURL });
    instance.interceptors.request.use(attachAuthToken);

    apiInstances.set(baseURL, instance);

    return instance;
};

export const logApiError = (context: string, error: unknown): void => {
    if (axios.isAxiosError(error)) {
        if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
            return;
        }
        console.error(`${context}:`, error.response?.data || error.message);
        return;
    }

    if (error instanceof Error) {
        if (error.name === 'AbortError') {
            return;
        }
        console.error(`${context}:`, error.message);
        return;
    }

    if (typeof DOMException !== 'undefined' && error instanceof DOMException && error.name === 'AbortError') {
        return;
    }

    console.error(`${context}:`, error);
};
